import type { VocabularyBackupCadence } from "./user.js";
import type { CidianDisplayEntry } from "../site-search/cidian-display-entry.js";
import type { SavedCidianEntry } from "./saved-cidian-entry.js";
import type { StudyState } from "../fuxi/study-state.js";
import type { TagCount } from "./saved-word-tag.js";
import { type OwnListChoice, choicesHolding } from "./own-list-choices.js";

/**
 * A word a reader has kept from the 词典, and what a press about one did.
 *
 * The headword and the fields needed to draw it are stored together. Hugo
 * publishes the canonical object that supplies those fields. A missing object
 * is refused, and its `word` value is authoritative where a redirecting
 * writing was posted.
 */

/**
 * What a press on a save control did.
 *
 * `refused` is the reader at the cap without Pro. `unknown` is a word the 词典
 * publishes no entry for, which writes nothing and is what a form typed by
 * somebody else arrives as. `readerSettings` and `ProPage` both take that shape
 * with a value they do not recognise.
 */
export type SaveOutcome = "saved" | "removed" | "refused" | "unknown";

/** What a press did, and to which entry. */
export interface SaveResult {
  readonly outcome: SaveOutcome;

  /** The entry it was about, absent where the 词典 has none. */
  readonly entry?: CidianDisplayEntry;

  /**
   * How many words a free account holds once this save has gone through.
   *
   * Absent for a member and for anything but a save. The press that takes a
   * free learner to fifty, a hundred or two hundred and fifty words is
   * congratulated (CB-61), and `src/user/milestones.ts` says when.
   */
  readonly freeHeld?: number;
}

/** One word a reader holds, as the list draws it. */
export interface SavedEntry {
  /** The entry the section publishes, which everything drawn comes from. */
  readonly entry: CidianDisplayEntry;

  /** The tags the reader has put on it, alphabetically. */
  readonly tags: readonly string[];

  /**
   * The reader's own Lists and parts holding it, in the order their menu
   * offers them. Absent where nothing asked, which draws as none.
   */
  readonly lists?: readonly OwnListChoice[];

  /**
   * Whether the reader prioritised it for 复习, which the row marks. See
   * `./priority-markup.ts`.
   */
  readonly prioritised?: boolean;
}

/**
 * The words a list draws, each with the tags the reader put on it.
 *
 * A word whose entry the section has since dropped is left out. The list is the
 * entries rather than the words, and a row with nothing behind it is a row with
 * no reading, no meaning and nowhere to go.
 * @param properties - The words in order, their tags, and the section.
 * @returns One entry per word the 词典 still publishes.
 */
export function savedEntries(properties: {
  held: readonly SavedWord[];
  tags: ReadonlyMap<string, readonly string[]>;
  entryOf: (word: SavedWord) => CidianDisplayEntry | undefined;
  ownLists?: readonly OwnListChoice[];
}): readonly SavedEntry[] {
  const choices = properties.ownLists ?? [];

  return properties.held.flatMap((saved) => {
    const entry = properties.entryOf(saved);

    return entry === undefined
      ? []
      : [
          {
            entry,
            tags: properties.tags.get(saved.word) ?? [],
            lists: choicesHolding(choices, saved.lists),
            ...(saved.study?.prioritisedAt === undefined
              ? {}
              : { prioritised: true }),
          },
        ];
  });
}

/** What a reader holds, including its stored dictionary snapshot. */
/** A reader's emailed backup, as the page under their list shows it. */
export interface SavedBackup {
  /** How often it goes out. */
  readonly cadence: VocabularyBackupCadence;

  /** Which day of that cycle it goes out on, in words. */
  readonly day: string;
}

export interface SavedCollection {
  /** Their words, newest first, each with the tags they put on it. */
  readonly entries: readonly SavedEntry[];

  /** Every tag they hold, alphabetically, with what each covers. */
  readonly tags: readonly TagCount[];

  /** Every List and part of their own, each List followed by its parts. */
  readonly ownLists: readonly OwnListChoice[];

  /** Whether they have room to make another List of their own. */
  readonly ownListRoom: boolean;

  /** Whether they hold Pro 专业, which is whether the cap applies. */
  readonly pro: boolean;

  /** How many words they hold, which is the count the cap is against. */
  readonly count: number;

  /**
   * Their emailed backup, where they have asked for one.
   *
   * Read here because the panel offering it sits under the download links on
   * the same page, and the membership beside it comes from the same user item.
   * A second read for one field would be a round trip for nothing.
   *
   * The day comes with it rather than being worked out by whatever draws the
   * panel, so nothing outside `vocabulary-backup-day.ts` knows that a reader's
   * own id is what decides it.
   */
  readonly backup: SavedBackup | undefined;
}

/** One word a reader holds. */
export interface SavedWord {
  /** The 词典 headword, as the section publishes it. */
  readonly word: string;

  /** When it was saved, which is what the list is ordered by. */
  readonly savedAt: Date;

  /** Display fields stored with the word, absent on an item awaiting backfill. */
  readonly entry?: SavedCidianEntry;

  /**
   * The import that wrote it, where an import did.
   *
   * **What makes an undo exact.** An import writes hundreds of words in one
   * act, and #703 takes exactly those back out again. The tag every import puts
   * on its words very nearly says which they are, and misses by the reader who
   * types a tag they were already using. Naming the import on the word settles
   * it outright.
   *
   * Absent on a word a reader saved by pressing the control, and gone from one
   * they removed and saved again, which is the right answer both times. See
   * `import-undo-writes.ts`.
   */
  readonly importId?: string;

  /**
   * What 复习 has made of the word, absent on one it has yet to reach.
   *
   * Which questions already have a card and when a session start last found
   * nothing more to ask. The row carries it so that a session start reads the
   * head of one index partition to find material, where it read the whole
   * list and every card. `src/fuxi/study-state.ts` has what each half means.
   */
  readonly study?: StudyState;

  /**
   * The reader's own Lists 词单 holding the word, by path.
   *
   * Written by `OwnListStore` in the transaction that writes the member, and
   * carried through every read and rewrite of the row so that a write of the
   * whole row keeps it. A path naming a List that has since been deleted is
   * read as naming nothing, since deleting a List rewrites these rows after
   * its own. Absent on a word in none.
   */
  readonly lists?: readonly string[];
}

/**
 * How many words a reader without Pro 专业 may hold.
 *
 * The cap is a product line and this is the one place it is written. A `Limit`
 * on the query keeps the count that this bounds to the cap however many the
 * reader turns out to hold. See `SavedWordStore.count`.
 *
 * **It is the size of HSK 1.** A free account is sized so that somebody can
 * study HSK 1 within it and needs Pro to go further, which is the rule #1075
 * set for every free limit. The 2025 syllabus grades 300 words at the band,
 * and `free-limits.iso.test.ts` holds this number to the committed word list,
 * so a syllabus that moves the band fails the test rather than leaving the cap
 * meaning nothing.
 *
 * **It was a hundred until #702 and a thousand until #1075.** A hundred was
 * too few to keep a list in, and a reader who reached it on their first
 * afternoon chose between deleting something and paying before the site had
 * been useful once. A thousand sold nothing and bounded only what a large free
 * population costs (ten thousand free readers at it came to $8.63 a month, most
 * of it the backup vault, and docs/dynamodb.md has the arithmetic). Three
 * hundred is a number a reader can understand, and the one the free tier is
 * meant to be outgrown at.
 *
 * **A list already past the cap loses nothing.** It stays readable, filterable
 * and exportable, and takes no new word until it is under the cap or the reader
 * subscribes. That is the state a lapse puts a list in, and `capOffer` in
 * `ci-notices-markup.ts` says so.
 */
export const freeSavedWords = 300;

/**
 * How many words a member may hold.
 *
 * **Pro 专业 was uncapped until the 词典 outgrew a single download.** A whole
 * list comes back out as one file and a Lambda may answer a synchronous
 * invocation with 6 MB, and the largest list anybody can hold is every entry
 * the section publishes. At 41,003 entries that was already past the limit, and
 * a full CC-CEDICT import takes it to about 29 MB. `ci-export-parts.ts` divides
 * a file that does not fit, and this bounds how far it ever has to.
 *
 * **Twenty thousand is chosen as a number no learner reaches.** A reader saving
 * ten words a day every day arrives in five and a half years. The largest
 * vocabulary a fluent adult native speaker is credited with is in that region,
 * and a list that long has stopped being a study list. Somebody who does reach
 * it can take the whole thing away as a file and clear what they have finished
 * with. Both acts are safe.
 *
 * **It is a limit on saving and never on taking the list away.** A member who
 * reaches it downloads all twenty thousand words, and so does a member who
 * lapses afterwards. See `ci-download.ts`.
 *
 * **No offer of Pro names it.** Hugh decided on 24 October 2026 that Pro is
 * offered with no limit on saved words and this stays a ceiling for the few who
 * ever reach it. `/pro/`, the account panel and the emails say "no limit", and
 * only a member at the cap is told the number, by `capOffer`.
 */
export const proSavedWords = 20_000;

/**
 * How many words this reader may hold.
 *
 * @param pro - Whether they hold Pro 专业.
 * @returns The cap that applies to them.
 */
export function savedWordCapOf(pro: boolean): number {
  return pro ? proSavedWords : freeSavedWords;
}

/**
 * The words newest first, which is the order a list is read in.
 *
 * The base table sorts a reader's collection by headword, that being the sort
 * key. Recency is an attribute on the item, and a page holding every word it
 * draws can order them here for nothing. An index keyed on the instant would
 * buy paging by recency and cost a read that can never be consistent, which is
 * the trade docs/dynamodb.md records.
 * Two words saved in the same millisecond fall back to the headword, so a list
 * drawn twice reads the same way twice. A test moving a simulated clock by hand
 * is where that happens.
 * @param words - What the reader holds, in whatever order they arrived.
 * @returns The same words, newest first.
 */
export function newestFirst(words: readonly SavedWord[]): readonly SavedWord[] {
  return [...words].sort(
    (one, other) =>
      other.savedAt.getTime() - one.savedAt.getTime() ||
      one.word.localeCompare(other.word),
  );
}
